@if (isVisible() && !isMinimized()) {
  <!--
    Non-blocking side drawer: no mask, the rest of the app stays interactive.
    Sits above the footer (h-12) so the minimized pill remains reachable.
  -->
  <aside
    class="dev-agent-drawer fixed top-3 right-3 bottom-14 z-[990] w-[440px] max-w-[calc(100vw-1.5rem)] flex flex-col rounded-2xl border border-primary-border bg-component-inner shadow-2xl overflow-hidden @container"
    role="complementary"
    [attr.aria-label]="config.texts.title"
  >
    <!-- ============ HEADER ============ -->
    <header class="dev-agent-header relative shrink-0 px-4 pt-4 pb-3 text-white">
      <div class="flex items-start gap-3">
        <!-- Avatar with status ring -->
        <div
          class="relative w-11 h-11 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center shrink-0"
        >
          <i class="fa-solid fa-robot text-lg"></i>
          @if (isRunning()) {
            <span
              class="absolute -inset-1 rounded-[18px] border-2 border-white/20 border-t-white animate-spin"
            ></span>
          }
          @if (isComplete()) {
            <i
              class="absolute -bottom-1 -right-1 fa-solid fa-circle-check text-emerald-300 text-[15px] bg-indigo-700 rounded-full"
            ></i>
          }
          @if (hasError()) {
            <i
              class="absolute -bottom-1 -right-1 fa-solid fa-circle-exclamation text-rose-300 text-[15px] bg-indigo-700 rounded-full"
            ></i>
          }
        </div>

        <div class="flex-1 min-w-0">
          <h2 class="text-base font-bold leading-tight m-0 truncate !text-white">
            {{ config.texts.title }}
          </h2>
          <div class="mt-1 flex items-center gap-2 flex-wrap">
            <span class="text-[11px] text-white/75">{{
              config.texts.subtitle
            }}</span>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
              [ngClass]="{
                'bg-white/20 text-white': runStatus() === 'ready',
                'bg-sky-300/25 text-sky-100': runStatus() === 'running',
                'bg-emerald-300/25 text-emerald-100': runStatus() === 'done',
                'bg-rose-300/30 text-rose-100': runStatus() === 'failed',
              }"
            >
              <span
                class="w-1.5 h-1.5 rounded-full"
                [ngClass]="{
                  'bg-white': runStatus() === 'ready',
                  'bg-sky-300 animate-pulse': runStatus() === 'running',
                  'bg-emerald-300': runStatus() === 'done',
                  'bg-rose-300': runStatus() === 'failed',
                }"
              ></span>
              @switch (runStatus()) {
                @case ('ready') {
                  {{ config.texts.statusReady }}
                }
                @case ('running') {
                  {{ config.texts.statusRunning }}
                }
                @case ('done') {
                  {{ config.texts.statusDone }}
                }
                @case ('failed') {
                  {{ config.texts.statusFailed }}
                }
              }
            </span>
          </div>
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <button
            type="button"
            id="dev-agent-minimize"
            (click)="minimizeAgent()"
            class="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
            nz-tooltip
            [nzTooltipTitle]="config.texts.minimizeTooltip"
          >
            <i class="fa-solid fa-window-minimize text-xs"></i>
          </button>
          <button
            type="button"
            id="dev-agent-close"
            (click)="requestClose()"
            class="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
            nz-tooltip
            [nzTooltipTitle]="config.texts.closeTooltip"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      @if (!showConfig()) {
        <div class="mt-3">
          <div
            class="flex items-center justify-between text-[11px] text-white/80 mb-1.5"
          >
            <span>
              {{ config.texts.stepOf }} {{ relevantStepPosition() }}
              {{ config.texts.stepOfSeparator }} {{ relevantStepCount() }}
            </span>
            <span class="font-semibold tabular-nums">{{ progressPercent() }}%</span>
          </div>
          <div class="h-1.5 rounded-full bg-white/20 overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-700 ease-out"
              [ngClass]="
                hasError()
                  ? 'bg-rose-300'
                  : isComplete()
                    ? 'bg-emerald-300'
                    : 'bg-white dev-agent-shimmer'
              "
              [style.width.%]="progressPercent()"
            ></div>
          </div>
        </div>
      }
    </header>

    <!-- ============ BODY ============ -->
    <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 py-4">
      @if (showConfig()) {
        <!-- Intro -->
        <p class="text-sm text-secondary-text leading-relaxed m-0">
          {{ config.texts.shortDescription }}
        </p>
        <details class="dev-agent-details mt-2 group">
          <summary
            class="text-xs font-semibold text-indigo-500 cursor-pointer select-none inline-flex items-center gap-1"
          >
            <i
              class="fa-solid fa-chevron-right text-[9px] transition-transform group-open:rotate-90"
            ></i>
            {{ config.texts.learnMore }}
          </summary>
          <p class="mt-2 text-xs text-secondary-text leading-relaxed">
            {{ config.texts.description }}
          </p>
        </details>

        <!-- Capabilities -->
        <h3
          class="mt-5 mb-2 text-[11px] font-bold uppercase tracking-wider text-secondary-text"
        >
          {{ config.texts.capabilitiesTitle }}
        </h3>
        <div class="grid grid-cols-1 @xs:grid-cols-2 gap-2">
          @for (cap of capabilities; track cap.title) {
            <div
              class="flex items-start gap-2.5 p-2.5 rounded-xl border border-primary-border bg-alt-background/60"
            >
              <span
                class="w-8 h-8 rounded-lg text-white text-xs flex items-center justify-center shrink-0 shadow-sm"
                [ngClass]="cap.tileClass"
                ><i [class]="cap.icon"></i
              ></span>
              <div class="min-w-0">
                <div class="text-xs font-semibold text-primary-text leading-tight">
                  {{ cap.title }}
                </div>
                <div class="text-[11px] text-secondary-text leading-snug mt-0.5">
                  {{ cap.description }}
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Job selection -->
        <h3
          class="mt-6 mb-2 text-[11px] font-bold uppercase tracking-wider text-secondary-text"
        >
          {{ config.texts.selectActionTitle }}
        </h3>
        <div class="flex flex-col gap-2" role="radiogroup">
          @for (option of actionOptions; track option.value) {
            @let selected = selectedAction() === option.value;
            <button
              type="button"
              role="radio"
              [attr.aria-checked]="selected"
              [attr.id]="'dev-agent-action-' + option.value"
              (click)="selectedAction.set(option.value)"
              class="dev-agent-option relative text-left flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer"
              [ngClass]="
                selected
                  ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/30'
                  : 'border-primary-border hover:border-secondary-border hover:bg-alt-background/60'
              "
            >
              <span
                class="w-9 h-9 rounded-xl text-white text-sm flex items-center justify-center shrink-0 shadow-sm"
                [ngClass]="option.tileClass"
                ><i [class]="option.icon"></i
              ></span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-semibold text-primary-text">{{
                    option.title
                  }}</span>
                  @if (option.badge) {
                    <span
                      class="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide bg-primary/10 text-primary"
                      >{{ option.badge }}</span
                    >
                  }
                </div>
                <p class="text-[11px] text-secondary-text leading-snug mt-0.5 mb-2">
                  {{ option.description }}
                </p>
                <!-- Mini pipeline preview -->
                <div class="flex items-center gap-1">
                  @for (step of config.steps; track step.id) {
                    @let included = option.stepIds.includes(step.id);
                    <span
                      class="w-5 h-5 rounded-md flex items-center justify-center text-[9px] transition-colors"
                      [ngClass]="
                        included
                          ? selected
                            ? 'bg-primary text-white'
                            : 'bg-alt-background text-primary-text border border-secondary-border'
                          : 'bg-transparent text-secondary-text/40 border border-dashed border-primary-border'
                      "
                      nz-tooltip
                      [nzTooltipTitle]="step.phase"
                      ><i [class]="step.icon"></i
                    ></span>
                  }
                </div>
              </div>
              <span
                class="w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                [ngClass]="
                  selected ? 'border-primary' : 'border-secondary-border'
                "
              >
                @if (selected) {
                  <span class="w-2 h-2 rounded-full bg-primary"></span>
                }
              </span>
            </button>
          }
        </div>

        <!-- Branch -->
        <h3
          class="mt-6 mb-2 text-[11px] font-bold uppercase tracking-wider text-secondary-text flex items-center gap-2"
        >
          {{ config.texts.branchTitle }}
          <span
            class="normal-case tracking-normal font-medium text-[10px] px-1.5 py-0.5 rounded bg-alt-background"
            >{{ config.texts.branchOptional }}</span
          >
        </h3>
        <div class="relative">
          <i
            class="fa-solid fa-code-branch absolute left-3 top-1/2 -translate-y-1/2 text-secondary-text text-xs"
          ></i>
          <input
            type="text"
            id="dev-agent-branch"
            class="w-full h-10 pl-8 pr-3 rounded-xl border text-sm font-mono bg-input-background text-primary-text border-primary-border focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition-all"
            [placeholder]="config.texts.branchPlaceholder"
            [value]="selectedBranch()"
            (input)="selectedBranch.set($any($event.target).value)"
          />
        </div>

        <!-- Backend status -->
        <h3
          class="mt-6 mb-2 text-[11px] font-bold uppercase tracking-wider text-secondary-text flex items-center gap-2"
        >
          <i class="fa-solid fa-server"></i>
          {{ config.texts.backendStatusTitle }}
        </h3>
        <div class="min-h-[44px] flex flex-col justify-center">
          @if (isLoadingBackendStatus()) {
            <div class="flex justify-center p-2">
              <nz-spin nzSimple></nz-spin>
            </div>
          } @else if (backendContainers().length === 0) {
            <div
              class="text-xs text-secondary-text text-center p-3 rounded-xl border border-dashed border-primary-border"
            >
              {{ config.texts.noContainers }}
            </div>
          } @else {
            <div class="flex flex-col gap-1.5">
              @for (container of backendContainers(); track $index) {
                <div
                  class="px-3 py-2 rounded-xl border border-primary-border bg-alt-background/60 flex justify-between items-center gap-2 text-xs"
                >
                  <span class="flex items-center gap-2 min-w-0">
                    <span
                      class="w-2 h-2 rounded-full shrink-0"
                      [ngClass]="
                        container.State === 'running'
                          ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.2)]'
                          : 'bg-rose-500'
                      "
                    ></span>
                    <span
                      class="font-mono text-primary-text truncate"
                      [title]="container.Names"
                      >{{ container.Names }}</span
                    >
                  </span>
                  <span
                    class="px-2 py-0.5 rounded-md font-bold uppercase text-[10px] shrink-0"
                    [ngClass]="
                      container.State === 'running'
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
                        : 'bg-rose-500/10 text-rose-600 dark:text-rose-300'
                    "
                  >
                    {{ container.State }}
                  </span>
                </div>
              }
            </div>
          }
        </div>

        <!-- Recent deployments -->
        <h3
          class="mt-6 mb-2 text-[11px] font-bold uppercase tracking-wider text-secondary-text flex items-center gap-2"
        >
          <i class="fa-solid fa-clock-rotate-left"></i>
          {{ config.texts.recentDeploymentsTitle }}
        </h3>
        <div class="flex flex-col justify-center">
          @if (isLoadingDeployments()) {
            <div class="flex justify-center p-2">
              <nz-spin nzSimple></nz-spin>
            </div>
          } @else if (!recentDeployments() || recentDeployments().length === 0) {
            <div
              class="text-xs text-secondary-text text-center p-3 rounded-xl border border-dashed border-primary-border"
            >
              {{ config.texts.noDeployments }}
            </div>
          } @else {
            <ol class="relative flex flex-col gap-2 m-0 p-0 list-none">
              @for (deploy of recentDeployments(); track $index) {
                <li class="flex gap-3">
                  <span
                    class="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ring-4"
                    [ngClass]="
                      deploy.status === 'successful'
                        ? 'bg-emerald-500 ring-emerald-500/15'
                        : deploy.status === 'failure'
                          ? 'bg-rose-500 ring-rose-500/15'
                          : 'bg-sky-500 ring-sky-500/15'
                    "
                  ></span>
                  <div
                    class="flex-1 min-w-0 px-3 py-2 rounded-xl border border-primary-border bg-alt-background/60 flex flex-col gap-1 text-xs"
                  >
                    <div class="flex justify-between items-center gap-2">
                      <span
                        class="font-semibold text-primary-text truncate flex items-center gap-1.5"
                        ><i class="fa-solid fa-code-branch text-[10px] text-secondary-text"></i
                        >{{ deploy.branch || config.texts.latestVersion }}</span
                      >
                      <span
                        class="px-1.5 py-0.5 rounded-md font-bold uppercase shrink-0 text-[9px]"
                        [ngClass]="
                          deploy.status === 'successful'
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
                            : deploy.status === 'failure'
                              ? 'bg-rose-500/10 text-rose-600 dark:text-rose-300'
                              : 'bg-sky-500/10 text-sky-600 dark:text-sky-300'
                        "
                      >
                        {{ deploy.status }}
                      </span>
                    </div>
                    <div
                      class="flex justify-between items-center gap-2 text-secondary-text text-[10px]"
                    >
                      <span class="truncate"
                        ><i class="fa-solid fa-user mr-1"></i>
                        {{
                          deploy.createdBy ||
                            deploy.createdById ||
                            config.texts.systemUser
                        }}</span
                      >
                      <span class="shrink-0"
                        ><i class="fa-regular fa-calendar mr-1"></i>
                        {{ deploy.createdDate | date: 'short' }}</span
                      >
                    </div>
                  </div>
                </li>
              }
            </ol>
          }
        </div>
      } @else {
        <!-- ============ RUNNING / RESULT ============ -->
        <h3
          class="mb-3 text-[11px] font-bold uppercase tracking-wider text-secondary-text"
        >
          {{ config.texts.pipelineTitle }}
        </h3>
        <ol class="dev-agent-stepper relative m-0 p-0 list-none">
          @for (step of config.steps; track step.id; let last = $last) {
            @let state = getStepState(step.id);
            <li class="relative flex gap-3 pb-4" [class.pb-0]="last">
              @if (!last) {
                <span
                  class="absolute left-[15px] top-8 bottom-0 w-0.5 rounded-full"
                  [ngClass]="
                    state === 'done'
                      ? 'bg-gradient-to-b from-emerald-500 to-emerald-400'
                      : 'bg-primary-border'
                  "
                ></span>
              }
              <span
                class="relative z-[1] w-8 h-8 rounded-xl flex items-center justify-center text-xs shrink-0 transition-all duration-300"
                [ngClass]="
                  state === 'done'
                    ? 'bg-emerald-500 !text-white text-white shadow-sm'
                    : state === 'active'
                      ? 'bg-primary !text-white text-white shadow-md shadow-primary/30 dev-agent-pulse'
                      : state === 'error'
                        ? 'bg-rose-500 !text-white text-white shadow-sm'
                        : state === 'pending'
                          ? 'bg-alt-background text-secondary-text border border-primary-border'
                          : 'bg-transparent text-secondary-text/50 border border-dashed border-primary-border'
                "
                [style.color]="state === 'done' || state === 'active' || state === 'error' ? '#ffffff' : null"
              >
                @switch (state) {
                  @case ('done') {
                    <i class="fa-solid fa-check"></i>
                  }
                  @case ('active') {
                    <i [class]="step.icon"></i>
                  }
                  @case ('error') {
                    <i class="fa-solid fa-xmark"></i>
                  }
                  @default {
                    <i [class]="step.icon"></i>
                  }
                }
              </span>

              <div class="flex-1 min-w-0 pt-0.5">
                <div class="flex items-center gap-2 flex-wrap">
                  <span
                    class="text-[10px] font-bold uppercase tracking-wider"
                    [ngClass]="{
                      'text-emerald-600 dark:text-emerald-400': state === 'done',
                      'text-indigo-500': state === 'active',
                      'text-rose-500': state === 'error',
                      'text-secondary-text': state === 'pending' || state === 'skipped',
                    }"
                    >{{ step.phase }}</span
                  >
                  @if (step.id === 2 && projectVersion()?.versionNumber) {
                    <span
                      class="text-[10px] px-1.5 py-0.5 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 rounded-md font-mono"
                      >v{{ projectVersion().versionNumber }}</span
                    >
                  }
                  @if (state === 'skipped') {
                    <span
                      class="text-[9px] uppercase font-bold text-secondary-text border border-primary-border px-1 rounded"
                      >{{ config.texts.skipped }}</span
                    >
                  }
                  @if (state === 'active') {
                    <i class="fa-solid fa-circle-notch fa-spin text-indigo-500 text-[10px]"></i>
                  }
                </div>
                <div
                  class="text-sm leading-snug mt-0.5"
                  [ngClass]="{
                    'text-primary-text font-medium': state === 'active' || state === 'error',
                    'text-primary-text': state === 'done',
                    'text-secondary-text opacity-70': state === 'pending',
                    'text-secondary-text line-through opacity-50': state === 'skipped',
                  }"
                >
                  {{ step.text }}
                </div>

                @let procs = getStepSubProcesses(step.id);
                @if (procs.length > 0) {
                  <div
                    class="mt-2 p-2 rounded-xl bg-alt-background/70 border border-primary-border flex flex-col gap-1.5"
                  >
                    @for (proc of procs; track $index) {
                      <div class="text-xs text-secondary-text flex items-start gap-2">
                        @if (proc.status === 'in-progress') {
                          <i
                            class="fa-solid fa-circle-notch fa-spin text-indigo-500 mt-0.5"
                          ></i>
                        }
                        @if (proc.status === 'successful') {
                          <i class="fa-solid fa-check text-emerald-500 mt-0.5"></i>
                        }
                        @if (proc.status === 'failure') {
                          <i class="fa-solid fa-xmark text-rose-500 mt-0.5"></i>
                        }
                        <div class="min-w-0 flex-1">
                          <span class="block text-primary-text/90 break-words">{{
                            proc.message
                          }}</span>
                          <span class="opacity-60 text-[10px]">{{
                            proc.timestamp | date: 'shortTime'
                          }}</span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </li>
          }
        </ol>

        @if (hasError()) {
          <div
            class="mt-5 p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-200 text-sm flex items-start gap-2 dev-agent-fade-in"
          >
            <i class="fa-solid fa-circle-exclamation mt-0.5"></i>
            <span>{{ config.texts.genericErrorMessage }}</span>
          </div>
        }

        @if (isComplete()) {
          <div class="mt-5 dev-agent-fade-in flex flex-col gap-3">
            <div
              class="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 text-sm flex items-start gap-2"
            >
              <i class="fa-solid fa-circle-check mt-0.5"></i>
              <span>{{ config.texts.successMessage }}</span>
            </div>

            @if (serverInfo()) {
              <div
                class="rounded-xl border border-primary-border overflow-hidden bg-alt-background/60"
              >
                <div
                  class="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-secondary-text border-b border-primary-border flex items-center gap-2"
                >
                  <i class="fa-solid fa-server"></i>
                  {{ config.texts.serverDetailsTitle }}
                </div>
                <dl class="m-0 divide-y divide-primary-border text-xs">
                  <div class="px-3 py-2 flex items-center justify-between gap-3">
                    <dt class="text-secondary-text">
                      {{ config.texts.serverNameLabel }}
                    </dt>
                    <dd class="m-0">
                      <span
                        class="px-2 py-0.5 bg-component-inner border border-primary-border rounded-md font-mono text-primary-text"
                        >{{ serverInfo()?.Names }}</span
                      >
                    </dd>
                  </div>
                  <div class="px-3 py-2 flex items-center justify-between gap-3">
                    <dt class="text-secondary-text">
                      {{ config.texts.stateLabel }}
                    </dt>
                    <dd class="m-0">
                      <span
                        class="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 rounded-md uppercase font-bold text-[10px]"
                        ><span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span
                        >{{ serverInfo()?.State }}</span
                      >
                    </dd>
                  </div>
                  <div class="px-3 py-2 flex items-start justify-between gap-3">
                    <dt class="text-secondary-text shrink-0">
                      {{ config.texts.statusLabel }}
                    </dt>
                    <dd class="m-0 text-right text-primary-text">
                      {{ serverInfo()?.Status }} {{ config.texts.statusAt }}
                      {{ serverInfo()?.CreatedAt | date: 'medium' }}
                    </dd>
                  </div>
                </dl>
              </div>
            }
          </div>
        }
      }
    </div>

    <!-- ============ FOOTER ============ -->
    <footer
      class="shrink-0 px-4 py-3 border-t border-primary-border bg-alt-background/50 flex items-center gap-2"
    >
      @if (showConfig()) {
        <span class="flex-1"></span>
        <button
          nz-button
          nzType="default"
          id="dev-agent-cancel"
          class="!rounded-lg"
          (click)="closeAgent()"
        >
          {{ config.texts.cancelButton }}
        </button>
        <button
          type="button"
          id="dev-agent-start"
          (click)="startConfiguredAgent()"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-hover-primary shadow-sm hover:shadow transition-all cursor-pointer border-none"
        >
          <i class="fa-solid fa-play text-xs"></i>
          {{ config.texts.startButton }}
        </button>
      } @else if (isRunning()) {
        <span
          class="flex-1 text-[11px] text-secondary-text flex items-center gap-1.5"
        >
          <i class="fa-solid fa-circle-info text-indigo-500"></i>
          {{ config.texts.backgroundHint }}
        </span>
        <button
          nz-button
          nzType="default"
          class="!rounded-lg"
          (click)="minimizeAgent()"
        >
          <i class="fa-solid fa-window-minimize mr-1.5 text-xs"></i>
          {{ config.texts.minimizeTooltip }}
        </button>
      } @else {
        <button
          nz-button
          nzType="default"
          id="dev-agent-logs"
          class="!rounded-lg"
          (click)="fetchAndShowLogs()"
          [nzLoading]="isLoadingLogs()"
        >
          <i class="fa-solid fa-terminal mr-1.5 text-xs"></i>
          {{ config.texts.viewLogsButton }}
        </button>
        <span class="flex-1"></span>
        <button
          nz-button
          id="dev-agent-done"
          class="!rounded-lg"
          [nzType]="isComplete() ? 'primary' : 'default'"
          [nzDanger]="hasError()"
          (click)="closeAgent()"
        >
          {{ config.texts.closeButton }}
        </button>
      }
    </footer>
  </aside>
}

<nz-modal
  [(nzVisible)]="isLogsModalVisible"
  [nzTitle]="logsModalTitleTpl"
  [nzFooter]="null"
  [nzWidth]="'100vw'"
  [nzStyle]="{ top: '0px', margin: '0px', padding: '0px' }"
  [nzBodyStyle]="{
    height: 'calc(100vh - 55px)',
    overflowY: 'auto',
    backgroundColor: '#0d1117',
    color: '#c9d1d9',
    padding: '0',
  }"
  (nzOnCancel)="isLogsModalVisible.set(false)"
>
  <ng-container *nzModalContent>
    <div
      *ngIf="isLoadingLogs()"
      class="flex justify-center items-center h-full"
    >
      <nz-spin nzSimple nzSize="large"></nz-spin>
    </div>

    <div
      *ngIf="!isLoadingLogs() && parsedLogs()"
      class="flex flex-col h-full w-full mx-auto border-x border-[#30363d] bg-[#0d1117]"
    >
      <div
        class="flex items-center justify-between bg-[#161b22] px-4 py-3 border-b border-[#30363d] shrink-0 sticky top-0 z-10"
      >
        <span class="font-bold text-[#e6edf3]">{{ parsedLogs()?.header }}</span>

        <button
          nz-button
          nzType="default"
          nzSize="small"
          class="!bg-[#21262d] !border-[#30363d] !text-[#c9d1d9] hover:!bg-[#30363d] transition-colors"
          (click)="toggleTimestamps()"
        >
          <i class="fa-regular fa-clock mr-2"></i>
          {{
            showLogTimestamps()
              ? config.texts.hideTimestamps
              : config.texts.showTimestamps
          }}
        </button>
      </div>

      <nz-collapse [nzBordered]="false" class="!bg-[#0d1117] !rounded-none">
        <nz-collapse-panel
          *ngFor="let step of parsedLogs()?.steps"
          [nzHeader]="stepHeaderTpl"
          [nzActive]="step.isExpanded"
          class="!bg-[#0d1117] !border-b !border-[#30363d]"
        >
          <ng-template #stepHeaderTpl>
            <div class="flex items-center gap-2 font-semibold text-[#e6edf3]">
              <i class="fa-solid fa-circle-check text-[#238636]"></i>
              {{ step.name }}
            </div>
          </ng-template>

          <div class="font-mono text-[12px] leading-[1.6] flex flex-col py-2">
            <div
              *ngFor="let line of step.lines"
              class="flex hover:bg-[#161b22] px-2 py-0.5 group"
            >
              <div
                class="w-12 text-right pr-4 text-[#6e7681] select-none shrink-0 group-hover:text-[#c9d1d9] transition-colors"
              >
                {{ line.lineNumber }}
              </div>
              <div
                *ngIf="showLogTimestamps() && line.timestamp"
                class="w-56 text-[#6e7681] shrink-0 select-none"
              >
                {{ line.timestamp }}
              </div>
              <div
                class="whitespace-pre-wrap break-words flex-1 text-[#c9d1d9]"
              >
                {{ line.content }}
              </div>
            </div>
          </div>
        </nz-collapse-panel>
      </nz-collapse>
    </div>

    <pre
      *ngIf="!isLoadingLogs() && !parsedLogs()"
      class="whitespace-pre-wrap font-mono text-sm leading-relaxed p-6"
      >{{ logsContent() }}</pre
    >
  </ng-container>
</nz-modal>

<ng-template #logsModalTitleTpl>
  <div class="flex items-center gap-3">
    <i class="fa-solid fa-terminal text-primary"></i>
    <span class="font-bold text-heading"
      >{{ config.texts.logsModalTitle }} - {{ workflowRunTitle() }}</span
    >
    <nz-tag
      *ngIf="workflowRunStatus()"
      [nzColor]="
        workflowRunStatus() === 'success'
          ? 'success'
          : workflowRunStatus() === 'failure'
            ? 'error'
            : 'processing'
      "
    >
      {{ workflowRunStatus() | uppercase }}
    </nz-tag>
  </div>
</ng-template>
